import Head from 'next/head';
import React, { useRef } from 'react';
import { SPECIALTY_FLU_VACCINATIONS } from '../../bookings/constants';
import { PageSection, PageType, SubSection } from '../../common/AnalyticsContext/TrackingData';
import { TrackingEvent } from '../../common/AnalyticsContext/TrackingEvent';
import { useAnalytics } from '../../common/AnalyticsContext/useAnalytics';
import { ShowingIndexProvider } from '../../common/components/Accordion/useShowingIndex';
import { LinkSection } from '../../common/components/Footer/LinkSection/LinkSection';
import { Heading } from '../../common/components/LandingPage/Heading/Heading';
import { LocationLinks } from '../../common/components/LandingPage/LocationLinks/LocationLinks';
import { Metro, STATE_PER_METRO } from '../../common/components/SearchHeader/constants';
import { makeSearchUrl } from '../../common/components/SearchHeader/urls/makeSearchUrl';
import { useEffectOnceOnMount } from '../../common/hooks/useEffectOnceOnCondition';
import { useRouter } from '../../common/hooks/useRouter';
import { FAQ } from './FAQ/FAQ';
import { FAQ_CONTENT } from './FAQ/FAQContent';
import { FluvaxCTA } from './FluvaxCTA/FluvaxCTA';
import {
  Container,
  Text,
  SubHeadingWrapper,
  SubHeading,
  SubText,
  FaqButton,
  Footer,
} from './Page.styles';

const HEADING_IMAGE_URL = 'public/images/Flu-Vax-2022-Landing-Page-Image_20220329014859.png';

const searchUrlHandler = (metro?: Metro) => {
  const locationProvided = metro ? { metro } : undefined;

  return makeSearchUrl({
    onlineAppointments: {},
    location: locationProvided,
    topic: {
      specialist: SPECIALTY_FLU_VACCINATIONS,
    },
    alwaysShowFilters: true,
  });
};

export const Page = () => {
  const router = useRouter();
  const faqRef = useRef<HTMLDivElement>(null);
  const faqContent = FAQ_CONTENT({ faqRef: faqRef });

  const { trackEvent } = useAnalytics({
    pageData: {
      section: PageSection.CONSUMER,
      subsection: SubSection.LANDING,
      pagePath: router?.pathname,
      pageType: PageType.LANDING,
    },
  });

  useEffectOnceOnMount(() => trackEvent(TrackingEvent.LANDING_PAGE_LOADED));

  const linkContainerText = (metro: Metro) =>
    `Flu Vaccination in ${metro} City, ${STATE_PER_METRO[metro]}`;

  return (
    <ShowingIndexProvider>
      <Head>
        <title>Book a Flu Vaccine Online Today</title>
        <meta
          name="description"
          content="Have you booked your flu vaccine? Be prepared this flu season by booking your 
          flu vaccine online with Healthengine. Pharmacy and GP options available."
        />
        <meta name="viewport" content="initial-scale=1.0,user-scalable=no" />
      </Head>
      <Container>
        <Heading
          titleText="Book a flu vaccine at your nearest pharmacy or general practice"
          imgxPath={HEADING_IMAGE_URL}
        >
          <Text>
            Be prepared this flu season by booking your flu vaccine online through Healthengine's
            directory.
          </Text>
          <FluvaxCTA
            trackingAttributes={{
              'data-event': 'cta-primary',
              'data-cta': 'Book a Flu Vaccine',
              'data-testid': 'Book Flu Vaccine Button',
            }}
          />
        </Heading>
      </Container>
      <SubHeadingWrapper>
        <SubHeading>
          Why should I get the flu vaccination?
          <SubText>
            Influenza, or the flu, is a very contagious infection of the airways. Although it can be
            a mild disease, it can also cause very serious illness in otherwise healthy people.
          </SubText>
          <SubText>
            With a sharp increase in severe flu cases this season, some states and territories have
            announced free flu vaccines for all individuals aged 6 months and over, until June 30
            2022.
          </SubText>
          <SubText>
            Vaccination is a safe and effective way to protect you from serious disease caused by
            influenza.
          </SubText>
          <SubText>
            For more information on the recent free flu vaccination announcements, please visit our{' '}
            <FaqButton onClick={() => faqRef.current?.scrollIntoView()}>
              FAQ section below.
            </FaqButton>
          </SubText>
        </SubHeading>
        <FluvaxCTA
          trackingAttributes={{
            'data-event': 'cta-primary',
            'data-cta': 'Book Flu Vaccine',
            'data-testid': 'Book Your Vaccine Button',
          }}
        />
      </SubHeadingWrapper>
      <FAQ questions={faqContent} />
      <Footer>
        <FluvaxCTA
          trackingAttributes={{
            'data-event': 'cta-primary',
            'data-cta': 'Book Flu Vaccine',
            'data-testid': 'Book Your Vaccine Button',
          }}
          CTALocation="FOOTER"
        />
      </Footer>
      <LocationLinks
        title="Find an online flu vaccination appointment by location"
        linkContainerText={linkContainerText}
        searchUrlHandler={searchUrlHandler}
      />
      <LinkSection />
    </ShowingIndexProvider>
  );
};
